import { motion } from "motion/react";
import { CircleCheck, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { subjectAccent, subjectTint } from "@/lib/app-types";
import type { PlanGroup } from "@/lib/lecture-plan";

export function RevisionPlanCard({
  group,
  index,
  today,
  busy,
  onOpen,
  onCompleteAll,
}: {
  group: PlanGroup;
  index: number;
  today: string;
  busy?: boolean;
  onOpen: (chapterId: string) => void;
  onCompleteAll: (ids: string[]) => void;
}) {
  const dueCount = group.revisions.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.25), type: "spring", stiffness: 260, damping: 26 }}
      className="glass rounded-[24px] p-5"
    >
      <button type="button" onClick={() => onOpen(group.chapterId)} className="w-full text-left">
        <div className="flex items-start gap-3">
          <span
            className={cn("mt-1.5 size-2.5 shrink-0 rounded-full bg-gradient-to-br", subjectAccent(group.subject))}
          />
          <div className="min-w-0 flex-1">
            <p className={cn("text-[11px] font-semibold uppercase tracking-wider", subjectTint(group.subject))}>
              {group.subject}
            </p>
            <h3 className="truncate text-[16px] font-bold tracking-tight">{group.chapter}</h3>
            <p className="mt-1 flex items-center gap-1.5 text-[12px] text-muted-foreground">
              <Layers className="size-3.5" />
              {dueCount} {dueCount === 1 ? "lecture" : "lectures"} due for revision
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-secondary/60 px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
            Tap to view
          </span>
        </div>
      </button>

      <motion.button
        type="button"
        whileTap={{ scale: 0.97 }}
        disabled={busy}
        onClick={() => onCompleteAll(group.revisions.map((r) => r.id))}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-primary/25 bg-primary/12 py-2.5 text-[13px] font-semibold text-primary disabled:opacity-50"
      >
        <CircleCheck className="size-4" /> Mark revised
      </motion.button>
    </motion.div>
  );
}

